/**
 * The agency's Data area (AgencyAnalytics' Integrations): which integrations
 * can be connected, and every account the agency has connected with its
 * status, last sync, Sync now and Pause. Attaching an account to a client
 * happens in that client's Data tab.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { ConnectSynledger } from './ConnectSynledger.tsx';
import { startGoogleConnect } from './google-connect.ts';
import {
  integrationsApi,
  type Connection,
  type DataSource,
  type SyncRun,
} from './integrations-api.ts';

const POLL_MS = 4000;
const when = new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });

interface Tile {
  key: string;
  name: string;
  what: string;
  state: 'available' | 'soon';
  note?: string;
}

const TILES: Tile[] = [
  {
    key: 'synledger',
    name: 'SynLedger',
    what: 'Invoices, payments, what is still owed and what sold.',
    state: 'available',
  },
  {
    key: 'ga4',
    name: 'Google Analytics 4',
    what: 'Sessions, users, channels, pages and conversions.',
    state: 'available',
  },
  {
    key: 'synteller',
    name: 'SynTeller',
    what: 'Daily sales by branch, payment method, staff and product.',
    state: 'soon',
    note: 'Arriving with the SynTeller rebuild.',
  },
];

const STATUS: Record<Connection['status'], string> = {
  PENDING_AUTH: 'No key — add one to connect',
  ACTIVE: 'Connected',
  PAUSED: 'Paused',
  ERROR: 'Needs attention',
  REVOKED: 'Disconnected',
};

function lastSync(connection: Connection, run: SyncRun | null | undefined): string {
  if (run?.status === 'RUNNING') return 'Syncing now…';
  if (run && (run.status === 'FAILED' || run.status === 'TIMED_OUT'))
    return `Last sync failed: ${run.error_message ?? 'unknown error'}`;
  if (connection.last_successful_sync_at)
    return `Synced ${when.format(new Date(connection.last_successful_sync_at))}`;
  return 'Not synced yet';
}

export function IntegrationsPage({
  organisationId,
  notice,
}: {
  organisationId: string;
  /** The outcome of a Google sign-in that just returned here. */
  notice?: { ok: boolean; text: string } | null;
}): ReactNode {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [connections, setConnections] = useState<Connection[] | null>(null);
  const [runs, setRuns] = useState<Record<string, SyncRun | null>>({});
  const [connecting, setConnecting] = useState<string | null>(null);
  const [rekey, setRekey] = useState<Connection | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [nextSources, nextConnections] = await Promise.all([
      integrationsApi.sources(organisationId),
      integrationsApi.connections(organisationId),
    ]);
    setSources(nextSources);
    setConnections(nextConnections);
    const entries = await Promise.all(
      nextConnections.map(
        async (item) => [item.id, await integrationsApi.lastRun(organisationId, item.id)] as const,
      ),
    );
    setRuns(Object.fromEntries(entries));
  }, [organisationId]);
  useEffect(() => {
    load().catch(() => setProblem('Integrations could not be loaded.'));
  }, [load]);
  const syncing = Object.values(runs).some((run) => run?.status === 'RUNNING');
  useEffect(() => {
    if (!syncing) return undefined;
    const timer = window.setInterval(() => void load().catch(() => undefined), POLL_MS);
    return () => window.clearInterval(timer);
  }, [syncing, load]);

  const act = (work: Promise<unknown>) =>
    work.then(load, (error: unknown) =>
      setProblem(error instanceof Error ? error.message : 'That did not work. Try again.'),
    );
  const sourceKey = (id: string) => sources.find((source) => source.id === id)?.source_key;
  const sourceName = (id: string) =>
    sources.find((source) => source.id === id)?.display_name ?? '—';

  return (
    <section className="ag-page" aria-labelledby="ag-area-title">
      <header className="ag-head">
        <h1 id="ag-area-title">Integrations</h1>
      </header>
      <div className="ag-body">
        {notice && (
          <p
            className={`rh-access-msg ${notice.ok ? 'rh-access-ok' : 'rh-access-error'}`}
            role={notice.ok ? 'status' : 'alert'}
          >
            {notice.text}
          </p>
        )}
        {problem && (
          <p className="rh-access-msg rh-access-error" role="alert">
            {problem}
          </p>
        )}
        <ul className="ig-tiles">
          {TILES.map((tile) => (
            <li key={tile.key} className="ig-tile">
              <strong>{tile.name}</strong>
              <p>{tile.what}</p>
              {tile.state === 'available' ? (
                <button
                  type="button"
                  className="btn small"
                  onClick={() =>
                    tile.key === 'ga4'
                      ? act(
                          // A connection still waiting for Google is reused, never duplicated.
                          startGoogleConnect(
                            organisationId,
                            connections?.find(
                              (item) =>
                                item.status === 'PENDING_AUTH' &&
                                sourceKey(item.data_source_id) === 'ga4',
                            )?.id,
                          ),
                        )
                      : setConnecting(tile.key)
                  }
                >
                  Connect
                </button>
              ) : (
                <small className="ig-soon">{tile.note}</small>
              )}
            </li>
          ))}
        </ul>
        <div className="ag-table-card">
          <p className="ag-table-meta">
            Connected accounts. Attach one to a client from that client’s Data tab.
          </p>
          <table className="ag-table">
            <thead>
              <tr>
                <th scope="col">Account</th>
                <th scope="col">Integration</th>
                <th scope="col">Status</th>
                <th scope="col">Last sync</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(connections ?? []).map((connection) => (
                <tr key={connection.id}>
                  <td>{connection.display_name}</td>
                  <td>{sourceName(connection.data_source_id)}</td>
                  <td>{STATUS[connection.status]}</td>
                  <td>{lastSync(connection, runs[connection.id])}</td>
                  <td className="ig-actions">
                    {connection.status === 'ACTIVE' && (
                      <button
                        type="button"
                        className="btn small secondary"
                        onClick={() =>
                          void act(integrationsApi.sync(organisationId, connection.id))
                        }
                      >
                        Sync now
                      </button>
                    )}
                    {sourceKey(connection.data_source_id) === 'ga4' && (
                      <button
                        type="button"
                        className="btn small secondary"
                        onClick={() => act(startGoogleConnect(organisationId, connection.id))}
                      >
                        {connection.status === 'PENDING_AUTH' ? 'Sign in with Google' : 'Reconnect'}
                      </button>
                    )}
                    {sourceKey(connection.data_source_id) === 'synledger' && (
                      <button
                        type="button"
                        className="btn small secondary"
                        onClick={() => setRekey(connection)}
                      >
                        {connection.status === 'PENDING_AUTH' ? 'Add key' : 'Update key'}
                      </button>
                    )}
                    {connection.status !== 'PENDING_AUTH' && (
                      <button
                        type="button"
                        className="btn small secondary"
                        onClick={() => {
                          if (
                            !window.confirm(
                              `Disconnect ${connection.display_name}? Its key is removed and it stops syncing. Data already synced stays.`,
                            )
                          )
                            return;
                          void act(integrationsApi.revoke(organisationId, connection.id));
                        }}
                      >
                        Disconnect
                      </button>
                    )}
                    {(connection.status === 'ACTIVE' || connection.status === 'PAUSED') && (
                      <button
                        type="button"
                        className="btn small secondary"
                        onClick={() =>
                          void act(
                            integrationsApi.setPaused(
                              organisationId,
                              connection.id,
                              connection.status === 'ACTIVE',
                            ),
                          )
                        }
                      >
                        {connection.status === 'ACTIVE' ? 'Pause' : 'Resume'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {connections?.length === 0 && (
                <tr>
                  <td colSpan={5} className="rh-source-none">
                    Nothing connected yet. Connect an integration above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {rekey && (
          <ConnectSynledger
            organisationId={organisationId}
            existing={{ id: rekey.id, name: rekey.display_name }}
            onConnected={() => {
              setRekey(null);
              void load();
            }}
            onClose={() => setRekey(null)}
          />
        )}
        {connecting === 'synledger' && (
          <ConnectSynledger
            organisationId={organisationId}
            onConnected={() => {
              setConnecting(null);
              void load();
            }}
            onClose={() => setConnecting(null)}
          />
        )}
      </div>
    </section>
  );
}
